// src/lib/managers/mail/MailReader.ts

import { FileAttachment } from "@microsoft/microsoft-graph-types";
import { EventBus } from "../../contexts";
import { logger, LOGS } from "../../utils";
import { ApiManager } from "../api/ApiManager";
import { ContactManager } from "../contact";
import {
    CustomViewManager,
    CustomViewSortOrder,
    ViewFilter,
} from "../customView";
import { DataManager } from "../dataManager/DataManager";
import { isThrottleError } from "../dataManager/graph/GraphThrottleError";
import { SentMailsCriteria } from "../dataManager/IDataApi";
import {
    parseUniqueMailvistaId,
    uniqueMailvistaId,
} from "./domain/mailIdentity";
import { MailRecord } from "./MailRecord";
import { MailSync } from "./MailSync";
import {
    Attachment,
    AttachmentMetadata,
    AttachmentPreview,
    AttachmentPreviewRef,
    Mail,
    MailThread,
} from "./types/mail";

export class MailReader {
    private eventBus: EventBus;

    private mailRecord: MailRecord;

    private mailSync: MailSync;

    private apiManager: ApiManager;

    private dataManager: DataManager;

    private customViewManager: CustomViewManager;

    private contactManager: ContactManager;

    /**
     * Déduplication des fetch de preview concurrents : clé de PJ → Promise partagée.
     * Jumeau de _inFlightFetches (fetch de mails). Auto-nettoyée au settle.
     */
    private readonly _inFlightPreviewFetches = new Map<string, Promise<void>>();

    constructor(
        eventBus: EventBus,
        mailRecord: MailRecord,
        mailSync: MailSync,
        apiManager: ApiManager,
        dataManager: DataManager,
        customViewManager: CustomViewManager,
        contactManager: ContactManager,
    ) {
        this.eventBus = eventBus;
        this.mailRecord = mailRecord;
        this.mailSync = mailSync;
        this.apiManager = apiManager;
        this.dataManager = dataManager;
        this.customViewManager = customViewManager;
        this.contactManager = contactManager;
    }

    public _cleanup(): void {
        this._inFlightPreviewFetches.clear();
    }

    /** Clé Record/dédup d'une preview de PJ = `${uniqueMailvistaId}::${attachmentId}`
     *  (uniqueMailvistaId contient déjà `internetMessageId::parentFolderId`). */
    private _attachmentPreviewKey(ref: AttachmentPreviewRef): string {
        return `${ref.uniqueMailvistaId}::${ref.attachmentId}`;
    }

    /**
     * Lance en fire-and-forget un worker pool (N=3) qui enrichit les mails portant
     * hasAttachment=true mais attachments=[] (cache incomplet depuis un listing).
     * Chaque mail enrichi émet 'mail-updated', patché par useGetMailsList dans le cache
     * React Query → le panel Documents se remplit progressivement sans bloquer le return.
     * Guard _attachmentsFetchedSet (clé COPIE = uniqueMailvistaId) : évite le re-fetch d'une copie
     * déjà enrichie. Le marquage est fait par getMailFullBodyContentWithAttachments, sur succès
     * uniquement — on ne pré-marque donc plus ici.
     */
    public _startAttachmentEnrichmentWorkers(mails: Mail[]): void {
        const mailsToEnrich = mails.filter(
            (mail) =>
                mail.hasAttachment === true &&
                mail.attachments.length === 0 &&
                !this.mailRecord._attachmentsFetchedSet.has(
                    uniqueMailvistaId(mail),
                ),
        );

        if (mailsToEnrich.length === 0) return;

        logger.info(
            [LOGS.MailManager],
            `_startAttachmentEnrichmentWorkers : ${mailsToEnrich.length} mail(s) à enrichir`,
        );

        const CONCURRENCY = 3;
        let idx = 0;
        const worker = async (): Promise<void> => {
            while (idx < mailsToEnrich.length) {
                const mail = mailsToEnrich[idx++];
                await this.getMailFullBodyContentWithAttachments(
                    mail.internetMessageId,
                ).catch((err) => {
                    logger.error(
                        [LOGS.MailManager],
                        `Enrichissement attachments échoué pour ${mail.internetMessageId}: ${err}`,
                    );
                });
            }
        };

        void Promise.all(
            Array.from(
                { length: Math.min(CONCURRENCY, mailsToEnrich.length) },
                () => worker(),
            ),
        );
    }

    public async getViewFilterMails(
        viewFilter: ViewFilter,
        limit: number | undefined = undefined,
    ): Promise<Mail[]> {
        logger.info(LOGS.MailManager, "getViewFilterMails");

        const viewFilterInternetMessageIds: string[] =
            await this.getViewFilterInternetMessageIds(viewFilter, limit);

        // ═══════════════════════════════════════════════════════════════════════════
        // CONVERSION DES INTERNETMESSAGEIDS EN OBJETS MAIL
        // ═══════════════════════════════════════════════════════════════════════════

        if (viewFilterInternetMessageIds.length === 0) {
            logger.info(
                LOGS.MailManager,
                "No internetMessageIds found, returning empty array",
            );
            return [];
        }

        // Fetch missing mails if any
        await this.mailSync.fetchMissingMailsIfAnyByInternetMessageIds(
            viewFilterInternetMessageIds,
            `MailReader.getViewFilterMails`,
        );

        // Get needed mails from Record
        const viewFilterMails: Mail[] = [];
        for (const internetMessageId of viewFilterInternetMessageIds) {
            const mail = this.mailSync._getCanonicalMail(internetMessageId);
            if (mail) {
                viewFilterMails.push(mail);
            } else {
                logger.warn(
                    [LOGS.MailManager],
                    `Mail avec internetMessageId ${internetMessageId} introuvable dans le cache après récupération`,
                );
            }
        }

        // Trie les mails par date d'envoi
        const sortedMails = this.mailSync._sortMailsBySentDateTime(
            viewFilterMails,
            CustomViewSortOrder.DESC,
        );

        logger.info(
            LOGS.MailManager,
            `getViewFilterMails - Returning ${sortedMails.length} mails`,
        );
        return sortedMails;
    }

    /**
     * Les mails envoyés qui répondent aux critères, `limit` au plus, triés par date d'envoi : boîte
     * « envoyes » de l'étape `lireMails` de l'assistant. Lus dans le dossier Envoyés et lui seul, puis
     * chargés par le chemin de la vue Dossier (projection directe, sans analyse ni base) : le chemin
     * canonique ne connaît que la boîte de réception, un mail envoyé y est « introuvable ».
     *
     * Un mail trouvé qui ne se charge pas lève : une lecture incomplète n'est jamais rendue comme une
     * liste plus courte, encore moins comme « aucun mail ».
     */
    public async getSentMails(
        criteria: SentMailsCriteria,
        limit: number,
    ): Promise<Mail[]> {
        logger.info(LOGS.MailManager, "getSentMails");

        const copyRefs = await this.dataManager.searchSentMailCopyRefs(
            criteria,
            limit,
        );
        if (copyRefs.length === 0) return [];

        await this.mailSync.fetchMissingFolderCopiesIfAny(
            copyRefs,
            "MailReader.getSentMails",
        );

        return copyRefs.map((ref) => {
            // La copie du dossier lu ; à défaut une autre copie du même message (Gmail : un message
            // envoyé à soi-même porte les deux libellés, et n'a qu'une copie).
            const mail =
                this.mailSync._getCopy(
                    ref.internetMessageId,
                    ref.parentFolderId,
                ) ??
                this.mailRecord.byInternetMessageId[ref.internetMessageId]
                    ?.values()
                    .next().value;
            if (!mail) {
                throw new Error(
                    `getSentMails - mail trouvé mais non chargé : ${ref.internetMessageId}`,
                );
            }
            return mail;
        });
    }

    public async getViewFilterInternetMessageIds(
        viewFilter: ViewFilter,
        limit: number | undefined = undefined,
    ): Promise<string[]> {
        const results = await this.mailSync._searchViewFilterResults(
            viewFilter,
            limit,
        );

        return results.map((result) => result.internetMessageId);
    }

    public getMailFromRecordByUniqueMailvistaId(uid: string): Mail | null {
        const { internetMessageId, parentFolderId } =
            parseUniqueMailvistaId(uid);

        // uid malformé (sans séparateur) → fallback sur la copie canonique du message
        if (!parentFolderId) {
            return this.mailSync._getMailFromRecordById(internetMessageId);
        }

        const mail = this.mailSync._getCopy(internetMessageId, parentFolderId);

        if (!mail) {
            logger.warn(
                [LOGS.MailManager],
                `Mail avec uniqueMailvistaId ${uid} introuvable dans le Record`,
            );
            return null;
        }

        return mail;
    }

    /**
     * Fetches the attachment list for a given mail, with one automatic retry on transient errors.
     *
     * Using the folder-scoped endpoint (parentFolderId provided) is preferred over the global
     * `/me/messages/{id}` endpoint: it bypasses Graph index lag and nested-folder resolution issues.
     *
     * Throttling 429 : ce filet manuel **ne rejoue PAS** sur un 429 — il est déjà géré
     * exhaustivement par le backoff de `GraphManager.callGraphEndPoint`. Sur une {@link GraphThrottleError}
     * (détectée via `isThrottleError`), on **dégrade gracieusement en `null`** (cohérent avec le contrat
     * ci-dessous : copie non marquée → retry autorisé plus tard), plutôt que de hammerer Graph.
     *
     * @param mailId        - Graph message ID
     * @param parentFolderId - Optional parent folder ID; enables the more reliable folder-scoped endpoint
     * @returns The attachment array, or `null` when the caller should abort gracefully:
     *          - 429: Graph throttled (handled below) — degrade gracefully, no manual retry
     *          - 404: message no longer exists in the mailbox (permanent — no point retrying)
     *          - retry failed: transient (non-429) error persisted after one attempt
     */
    private async _fetchMailAttachmentsWithRetry(
        mailId: string,
        parentFolderId?: string,
    ): Promise<FileAttachment[] | null> {
        try {
            return await this.dataManager.getMailAttachments(
                mailId,
                parentFolderId,
            );
        } catch (error) {
            // 429 déjà géré exhaustivement par le backoff de callGraphEndPoint → ne pas rejouer.
            // Dégradation gracieuse cohérente avec le contrat (null = échec transitoire, copie non marquée
            // → retry autorisé lors d'un appel ultérieur), plutôt qu'un throw.
            if (isThrottleError(error)) {
                logger.warn(
                    [LOGS.MailManager],
                    `Attachments throttled (429) for mail ${mailId} — skipping (no retry)`,
                );
                return null;
            }

            // 404 = message not found in mailbox — permanent error, retrying would waste 2 s
            if (String(error).includes("404")) {
                logger.error(
                    [LOGS.MailManager],
                    `getMailFullBodyContentWithAttachments: 404 for mail ${mailId} — skipping`,
                );
                return null;
            }

            // Transient error (network, 5xx) — retry once after a short delay
            logger.error(
                [LOGS.MailManager],
                `Error fetching attachments for mail ${mailId}: ${error}`,
            );
            logger.warn(LOGS.MailManager, "Retrying once after 2s delay ...");
            await new Promise((resolve) => setTimeout(resolve, 2000));
            try {
                return await this.dataManager.getMailAttachments(
                    mailId,
                    parentFolderId,
                );
            } catch (retryError) {
                // Second failure — return gracefully so the thread still displays without inline images
                logger.error(
                    [LOGS.MailManager],
                    `Retry also failed for mail ${mailId}: ${retryError}`,
                );
                return null;
            }
        }
    }

    /**
     * Dérive les métadonnées légères (AttachmentMetadata) depuis les PJ complètes d'un Mail chargé.
     *
     * ⚠️ EXCLUT les PJ inline (`isInline === true` : images CID intégrées au corps). Graph les remonte
     * dans `mail.attachments`, mais elles ne sont PAS des documents de CustomView. Le Backend les exclut
     * déjà (`MailContent.fromMail` → `.filter(isInline !== true)`) → on applique le MÊME filtre ici pour
     * une PARITÉ STRICTE warm-cache ⇄ fetch backend (M3, review PR #210) : sans ça, le contenu/compteur
     * d'une view diffère selon qu'un mail a été ouvert en thread (seed, avec inline) ou fetché via
     * /mails/content (sans inline). La dédup au read ne peut pas rattraper (AttachmentMetadata ne porte
     * pas `isInline`).
     */
    private _deriveAttachmentMetadata(mail: Mail): AttachmentMetadata[] {
        const uid = uniqueMailvistaId(mail);
        return (mail.attachments ?? [])
            .filter((att) => att.isInline !== true)
            .map((att) => ({
                uniqueMailvistaId: uid,
                attachmentId: att.id,
                name: att.name,
                size: att.size,
                type: att.type,
                contentId: att.contentId,
                sentAt:
                    att.sentAt == null
                        ? ""
                        : typeof att.sentAt === "number"
                          ? new Date(att.sentAt).toISOString()
                          : String(att.sentAt),
            }));
    }

    /**
     * Write-through (warm-cache) : alimente le Record view-content depuis un Mail thread DÉJÀ ENRICHI.
     * Écrit TOUJOURS un bundle COMPLET (attachments metadata + links + events) — jamais partiel.
     *
     * ⚠️ Pourquoi complet uniquement : la simple PRÉSENCE de la clé dans le Record gèle l'appel
     * `/mails/content` (cf. fetchMissingMailViewContentIfAny, gate `imid in Record`). Un seed PARTIEL
     * (links/events seuls, attachments encore vides parce que non enrichis) créerait une entrée qui
     * empêche à jamais le fetch backend — et si l'enrichissement échoue (Graph 404 / erreur persistante),
     * la CustomView sous-déclarerait définitivement les documents de ce mail. On ne seed donc que des
     * mails complets (attachments enrichis, éventuellement `[]`). Le thread écrase (source locale la plus
     * fraîche) ; un mail non encore enrichi n'a PAS d'entrée → une CustomView le fetchera via /mails/content.
     *
     * NB : on ne recopie PAS les previews base64 ici (duplication mémoire lourde, éagerement, pour un
     * bénéfice marginal) — la preview CustomView est récupérée à la demande via getAttachmentPreview(s).
     *
     * ⚠️ Alternative plus complexe (REPORTÉE — cf. Doc/AB#1727) : faire LIRE le thread
     * depuis le Record via des lecteurs SDK dédiés (getMailThread{Attachments,Links,Events}) et retirer la
     * dérivation front. Non fait ici : le front thread calcule depuis les mails chargés ; ce seed ne sert
     * (pour l'instant) qu'à réchauffer le Record pour les CustomViews.
     */
    private _seedMailViewContentFromMail(mail: Mail): void {
        const imid = mail.internetMessageId;
        // Estampille l'event avec le sentDateTime du mail porteur (même sémantique que le champ `sentAt`
        // renseigné par le Backend dans /mails/content) → le FrontEnd ordonne les copies d'une réunion par
        // date d'envoi du mail. Forme homogène entre write-through (ici) et fetch Backend. Cf. AB#1727.
        const mailSentAt =
            mail.sentDateTime == null
                ? ""
                : typeof mail.sentDateTime === "number"
                  ? new Date(mail.sentDateTime).toISOString()
                  : String(mail.sentDateTime);
        const attachments = this._deriveAttachmentMetadata(mail);
        // Observabilité M3 : trace les PJ inline exclues (parité Backend) — utile pour la validation sur
        // données réelles (le compteur documents du warm-cache doit coïncider avec celui du fetch backend).
        const inlineExcluded =
            (mail.attachments ?? []).length - attachments.length;
        if (inlineExcluded > 0) {
            logger.info(
                LOGS.AB1727,
                `[AB#1727-SDK] warm-cache seed(${imid}): ${inlineExcluded} PJ inline exclue(s) des documents (parité Backend, M3)`,
            );
        }
        this.mailRecord.mailViewContentRecordByInternetMessageId[imid] = {
            attachments,
            links: mail.links ?? [],
            events: mail.linkedEventDetails
                ? [{ ...mail.linkedEventDetails, sentAt: mailSentAt }]
                : [],
        };
    }

    public async getAttachmentPreview(
        ref: AttachmentPreviewRef,
    ): Promise<string | null> {
        const [preview] = await this.getAttachmentsPreviews([ref]);
        return preview?.preview ?? null;
    }

    public async getAttachmentsPreviews(
        refs: AttachmentPreviewRef[],
    ): Promise<AttachmentPreview[]> {
        logger.info(
            [LOGS.MailManager],
            `getAttachmentsPreviews(${refs.length})`,
        );

        // 1. Dédup de l'entrée par clé de PJ
        const uniqueRefs = new Map<string, AttachmentPreviewRef>();
        for (const ref of refs)
            uniqueRefs.set(this._attachmentPreviewKey(ref), ref);

        // 2. Diff contre le Record : ne fetcher que les previews absentes
        const missing: AttachmentPreviewRef[] = [];
        for (const [key, ref] of uniqueRefs) {
            if (!(key in this.mailRecord.attachmentPreviewsRecordByKey))
                missing.push(ref);
        }
        logger.info(
            LOGS.AB1727,
            `[AB#1727-SDK] getAttachmentsPreviews: requested=${refs.length} unique=${uniqueRefs.size} cacheHits=${uniqueRefs.size - missing.length} toFetch=${missing.length}`,
        );
        if (missing.length > 0) {
            await this._fetchMissingAttachmentPreviews(missing);
        }

        // 3. Servir depuis le Record (clés non peuplées = fetch échoué → omises, re-demandables plus tard)
        const result: AttachmentPreview[] = [];
        for (const [key, ref] of uniqueRefs) {
            const preview = this.mailRecord.attachmentPreviewsRecordByKey[key];
            if (preview !== undefined) {
                result.push({
                    uniqueMailvistaId: ref.uniqueMailvistaId,
                    attachmentId: ref.attachmentId,
                    preview,
                });
            }
        }
        logger.info(
            LOGS.AB1727,
            `[AB#1727-SDK] getAttachmentsPreviews done: resolved=${result.length}/${uniqueRefs.size} (omitted=${uniqueRefs.size - result.length})`,
        );
        return result;
    }

    /**
     * Fetch des previews manquantes avec déduplication des appels concurrents par clé de PJ.
     * Le Record n'est peuplé qu'au SUCCÈS (une PJ absente de la réponse reste re-demandable) — même
     * sémantique que le guard _attachmentsFetchedSet (marquage au succès uniquement).
     *
     * ⚠️ CHUNKING OBLIGATOIRE (HIGH-2, review PR #210) : le Backend cappe `attachments` à 50
     * (`/mails/attachments/previews` → téléchargements Graph base64 lourds). Un POST > 50 refs = HTTP 400
     * → TOUTES les previews du lot tombent en `null` silencieusement. On chunke donc à ≤ PREVIEW_BATCH_SIZE
     * et on borne la concurrence (pool) pour éviter la rafale de POST → fan-out Graph → 429, à l'image du
     * path content (fetchMissingMailViewContentIfAny).
     */
    private async _fetchMissingAttachmentPreviews(
        refs: AttachmentPreviewRef[],
    ): Promise<void> {
        // Partitionner : PJ déjà en vol (partager la promesse) vs à fetcher
        const inFlight: Promise<void>[] = [];
        const toFetch: AttachmentPreviewRef[] = [];
        for (const ref of refs) {
            const key = this._attachmentPreviewKey(ref);
            const existing = this._inFlightPreviewFetches.get(key);
            if (existing) inFlight.push(existing);
            else toFetch.push(ref);
        }

        if (toFetch.length > 0) {
            const PREVIEW_BATCH_SIZE = 50; // refs par requête POST /mails/attachments/previews (cap Backend)
            const MAX_CONCURRENT_CHUNKS = 4; // lots en vol simultanément (borne le fan-out Graph côté Backend)

            const chunks: AttachmentPreviewRef[][] = [];
            for (
                let startIdx = 0;
                startIdx < toFetch.length;
                startIdx += PREVIEW_BATCH_SIZE
            ) {
                chunks.push(
                    toFetch.slice(startIdx, startIdx + PREVIEW_BATCH_SIZE),
                );
            }

            // Une « gate » par lot : promesse enregistrée en in-flight (dédup par clé de PJ) dès maintenant,
            // même si l'exécution du lot est différée par le pool. Résolue quand le pool a traité le lot
            // (best-effort → toujours résolue, jamais rejetée).
            const gates = chunks.map(() => {
                let resolve!: () => void;
                const promise = new Promise<void>((r) => {
                    resolve = r;
                });
                return { promise, resolve };
            });
            chunks.forEach((chunk, ci) => {
                const gatePromise = gates[ci].promise;
                const keys = chunk.map((r) => this._attachmentPreviewKey(r));
                for (const key of keys)
                    this._inFlightPreviewFetches.set(key, gatePromise);
                gatePromise.finally(() => {
                    for (const key of keys)
                        this._inFlightPreviewFetches.delete(key);
                });
                inFlight.push(gatePromise);
            });

            // Pool borné : au plus MAX_CONCURRENT_CHUNKS lots traités en parallèle.
            let chunkIdx = 0;
            const chunkWorker = async (): Promise<void> => {
                while (chunkIdx < chunks.length) {
                    const ci = chunkIdx++;
                    try {
                        await this._fetchAttachmentPreviewsChunk(chunks[ci]); // best-effort : ne throw pas
                    } finally {
                        gates[ci].resolve();
                    }
                }
            };
            // Fire-and-forget : les workers avalent déjà toute erreur (chunk best-effort + finally resolve la
            // gate), + `.catch` no-op pour garantir zéro rejet non géré (cf. M4).
            void Promise.all(
                Array.from(
                    { length: Math.min(MAX_CONCURRENT_CHUNKS, chunks.length) },
                    () => chunkWorker(),
                ),
            ).catch(() => {
                /* best-effort : les gates restent résolues par le finally du worker */
            });
        }

        await Promise.allSettled(inFlight);
    }

    /**
     * Fetch + peuplement du Record pour UN lot de previews (≤ PREVIEW_BATCH_SIZE = cap Backend).
     * Best-effort : un échec de lot ne peuple rien pour ce lot (clés re-demandables au prochain appel) et
     * n'impacte pas les autres lots. Le Record n'est peuplé qu'au SUCCÈS (une PJ absente de la réponse
     * reste re-demandable).
     */
    private async _fetchAttachmentPreviewsChunk(
        refs: AttachmentPreviewRef[],
    ): Promise<void> {
        try {
            const previews = await this.apiManager.getAttachmentsPreviews(refs);
            for (const p of previews) {
                this.mailRecord.attachmentPreviewsRecordByKey[
                    this._attachmentPreviewKey(p)
                ] = p.preview;
            }
        } catch (error) {
            logger.error(
                [LOGS.MailManager],
                `_fetchAttachmentPreviewsChunk failed: ${error}`,
            );
            // Ne pas peupler le Record → retry autorisé au prochain appel
        }
    }

    public async getMailFullBodyContentWithAttachments(
        internetMessageId: string,
    ): Promise<void> {
        logger.info(
            [LOGS.MailManager],
            `Fetching attachments for mail ${internetMessageId}`,
        );

        const mail = this.mailSync._getCanonicalMail(internetMessageId);
        if (!mail || !mail?.id) {
            logger.error(
                [LOGS.MailManager],
                `Could not find mail corresponding to ${internetMessageId} in Record`,
            );
            throw `Could not find mail corresponding to ${internetMessageId} in Record`;
        }

        // Clé de niveau COPIE (uniqueMailvistaId = internetMessageId::parentFolderId). On la dérive via
        // la fonction du noyau plutôt que le champ optionnel mail.uniqueMailvistaId.
        const copyKey = uniqueMailvistaId(mail);

        // Guard niveau COPIE : si CETTE copie a déjà été enrichie avec succès dans la session, inutile
        // de re-fetcher. Une autre copie du même internetMessageId (ex. self-sent Inbox vs Sent) reste
        // enrichissable indépendamment.
        if (this.mailRecord._attachmentsFetchedSet.has(copyKey)) {
            return;
        }

        // Pass parentFolderId so Graph uses the folder-scoped endpoint, which is more reliable
        // than the global /me/messages/{id} endpoint (bypasses index lag and nested-folder issues).
        const attachments = await this._fetchMailAttachmentsWithRetry(
            mail.id,
            mail.parentFolderId,
        );
        if (attachments === null) {
            // Échec de fetch (404 sur id Graph périmé, ou erreur transitoire persistante après retry) :
            // NE PAS marquer la copie comme enrichie → un appel ultérieur pourra réessayer.
            logger.warn(
                [LOGS.MailManager],
                `getMailFullBodyContentWithAttachments: aucun attachment récupéré pour ${internetMessageId} (copie ${copyKey}) — copie non marquée, retry autorisé`,
            );
            return;
        }

        const inlineAttachments: Attachment[] = [];

        if (attachments && attachments.length > 0) {
            mail.attachments = attachments
                .filter((att: FileAttachment) => !att.isInline)
                .map((att: FileAttachment) => ({
                    id: att.id!,
                    name: att.name!,
                    size: att.size!,
                    type: att.contentType!,
                    path: att.contentBytes!,
                    preview: `data:${att.contentType};base64,${att.contentBytes}`,
                    // Content-ID stable (cf. AB#1681) : clé de dédup fiable côté front, insensible à la
                    // réécriture du conteneur lors d'un transfert (la taille, elle, varie).
                    contentId: att.contentId ?? undefined,
                    modifiedAt: att.lastModifiedDateTime!,
                    sentAt: mail.sentDateTime,
                }));

            // Toutes les PJ sont inline (images embarquées dans le body) — aucun document téléchargeable.
            // On corrige hasAttachment à false pour que _startAttachmentEnrichmentWorkers
            // n'essaie plus d'enrichir ce mail lors des prochains passages.
            if (mail.attachments.length === 0) {
                mail.hasAttachment = false;
                logger.info(
                    [LOGS.MailManager],
                    `Mail ${internetMessageId} : uniquement des PJ inline — hasAttachment corrigé à false`,
                );
            }

            // Client Side Filtering for inline attachments
            const filteredInlineAttachments = attachments.filter(
                (attachment: any) => attachment.isInline,
            );

            if (
                filteredInlineAttachments &&
                filteredInlineAttachments.length > 0
            ) {
                filteredInlineAttachments.forEach((graphAttachments: any) => {
                    const attachmentsObject: Attachment = {
                        contentBytes: graphAttachments.contentBytes,
                        name: graphAttachments.contentId,
                        contentType: graphAttachments.contentType,
                    };
                    inlineAttachments.push(attachmentsObject);
                });
            }

            const cleanMessageContent = mail.message;
            const cleanUniqueBodyContent = mail.uniqueBody;

            // TODO: À tester pour déterminer si cette étape de nettoyage est nécessaire
            // const cleanContent = mail.message
            //   .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '') // Suppression des styles CSS
            //   .replace(/<base[^>]*>[\s\S]*?/gi, '')           // Suppression des balises base

            // Initialisation du contenu final avec le contenu nettoyé
            let finalMessageContent: string = cleanMessageContent;
            let finalUniqueBodyContent: string = cleanUniqueBodyContent;

            // Traitement des pièces jointes inline si elles existent
            if (inlineAttachments && inlineAttachments.length > 0) {
                logger.info(
                    [LOGS.MailManager],
                    `Found ${inlineAttachments.length} inline attachments for mail ${mail.id}`,
                );

                // Remplacement des références CID par des données base64
                // Pattern de recherche : src="cid:nom_de_la_piece_jointe"
                finalMessageContent = cleanMessageContent.replace(
                    /src="cid:([^"]+)"/g,
                    (match, cidName) => {
                        // Recherche de la pièce jointe correspondante — match exact uniquement
                        const attachment = attachments.find((att) => {
                            return (
                                (att.name &&
                                    (att.name === cidName || // Match exact sur le CID complet
                                        att.name === `${cidName}@host`)) || // Variant @host legacy
                                (att.contentId && att.contentId === cidName) // Match exact sur contentId
                            );
                        });

                        // Si une pièce jointe correspondante est trouvée
                        if (attachment) {
                            logger.info(
                                [LOGS.MailManager],
                                `Found matching attachment for CID: ${cidName}`,
                            );
                            return `src="data:${attachment.contentType};base64,${attachment.contentBytes}"`;
                        }

                        // Si aucune correspondance n'est trouvée, conserver la référence originale
                        logger.info(
                            [LOGS.MailManager],
                            `No matching attachment found for CID: ${cidName}`,
                        );
                        return match;
                    },
                );

                // Remplacement des références CID par des données base64
                // Pattern de recherche : src="cid:nom_de_la_piece_jointe"
                finalUniqueBodyContent = cleanUniqueBodyContent.replace(
                    /src="cid:([^"]+)"/g,
                    (match, cidName) => {
                        // Recherche de la pièce jointe correspondante — match exact uniquement
                        const attachment = attachments.find((att) => {
                            return (
                                (att.name &&
                                    (att.name === cidName || // Match exact sur le CID complet
                                        att.name === `${cidName}@host`)) || // Variant @host legacy
                                (att.contentId && att.contentId === cidName) // Match exact sur contentId
                            );
                        });

                        // Si une pièce jointe correspondante est trouvée
                        if (attachment) {
                            logger.info(
                                [LOGS.MailManager],
                                `Found matching attachment for CID: ${cidName}`,
                            );
                            // Conversion en data URI base64 pour affichage direct dans le HTML
                            return `src="data:${attachment.contentType};base64,${attachment.contentBytes}"`;
                        }

                        // Si aucune correspondance n'est trouvée, conserver la référence originale
                        logger.info(
                            [LOGS.MailManager],
                            `No matching attachment found for CID: ${cidName}`,
                        );
                        return match;
                    },
                );

                // Replacing Mails Message and Unique Body
                mail.message = finalMessageContent;
                mail.uniqueBody = finalUniqueBodyContent;
            }

            // Les PJ inline viennent d'être retirées → la taille de tuile peut changer.
            mail.componentSize =
                this.mailSync._determineMailComponentSize(mail);

            // Cet emit est « contenu seul » (body/inline images/attachments) : il est nécessaire pour
            // que le front réaffiche le mail enrichi, mais il ne doit PAS faire réinsérer/re-matcher les
            // vues. On amorce la signature de vues juste avant → le CustomViewManager court-circuitera.
            this.customViewManager.primeMailViewSignature(mail);
            this.eventBus.emit("mail-updated", mail);
        } else {
            // Graph retourne 0 PJ malgré hasAttachment=true — donnée obsolète (PJ supprimée côté serveur ?).
            // On corrige le flag pour éviter tout re-fetch inutile dans cette session.
            mail.hasAttachment = false;
            logger.info(
                [LOGS.MailManager],
                `No attachment found for this mail ${internetMessageId} — hasAttachment corrigé à false`,
            );
        }

        // C (push) : propage ces PJ aux copies sœurs vides du même message (Record only). #1332
        this._propagateAttachmentsToSiblings(mail);

        // Aboutissement → on marque la copie (sur échec on est sorti plus haut sans marquer). #1332
        this.mailRecord._attachmentsFetchedSet.add(copyKey);

        // Write-through (warm-cache) : le mail est maintenant COMPLET (links/events du DTO + attachments
        // enrichis) → seed un bundle complet dans le Record view-content (le thread écrase). Cf.
        // _seedMailViewContentFromMail & Doc/AB#1727.
        this._seedMailViewContentFromMail(mail);
    }

    public async getMailThread(uniqueMailvistaId: string): Promise<MailThread> {
        logger.info(
            [LOGS.MailManager, LOGS.AnalysisLifecycle],
            `getMailThread(${uniqueMailvistaId}) - `,
        );

        // WS6b : signature copie-aware. Le thread est message-level → on dérive l'imid (parse), logique interne inchangée.
        const { internetMessageId } = parseUniqueMailvistaId(uniqueMailvistaId);
        let canonicalMail = this.mailSync._getCanonicalMail(internetMessageId);
        if (!canonicalMail) {
            // Mail pas encore chargé dans le Record (par exemple cité par une conversation de
            // l'assistant rouverte depuis l'historique, après un rechargement) : chargé à la volée.
            await this.mailSync.fetchMissingMailsIfAnyByInternetMessageIds(
                [internetMessageId],
                `MailReader.getMailThread`,
            );
            canonicalMail = this.mailSync._getCanonicalMail(internetMessageId);
        }
        if (!canonicalMail) {
            const errorMessage = ` !!! Mail with internetMessageId ${internetMessageId} not found in mailsRecord !!!`;
            logger.error([LOGS.MailManager], errorMessage);
            throw errorMessage;
        }

        const conversationId = canonicalMail.conversationId;
        if (!conversationId) {
            const errorMessage = ` !!! Mail with internetMessageId ${internetMessageId} has no conversationId !!!`;
            logger.error([LOGS.MailManager], errorMessage);
            throw errorMessage;
        }

        logger.info(
            [LOGS.MailManager, LOGS.AnalysisLifecycle],
            `  conversationId ${conversationId}`,
        );

        let conversationMailsInternetMessageIds: string[] = [];
        try {
            conversationMailsInternetMessageIds =
                await this.dataManager.getMailThreadInternetMessageIds(
                    conversationId,
                );
        } catch (error) {
            // 429 déjà géré exhaustivement par le backoff de callGraphEndPoint → ne pas rejouer
            if (isThrottleError(error)) throw error;
            logger.error(
                [LOGS.MailManager],
                `Error in getMailThread : ${error}`,
            );
            logger.warn(LOGS.MailManager, "Retrying once after 2s delay ...");

            await new Promise((resolve) => setTimeout(resolve, 2000));
            conversationMailsInternetMessageIds =
                await this.dataManager.getMailThreadInternetMessageIds(
                    conversationId,
                );
        }

        // Checks for missing mails in Record and adds them if necessary
        await this.mailSync.fetchMissingMailsIfAnyByInternetMessageIds(
            conversationMailsInternetMessageIds,
            `MailReader.getMailThread`,
        );

        const sortedConversationMailsInternetMessageIds =
            conversationMailsInternetMessageIds
                .map((internetMessageId) =>
                    this.mailSync._getCanonicalMail(internetMessageId),
                )
                .filter((mail): mail is Mail => mail !== undefined)
                // AB#1900 — masquage EXPLICITE des messages en corbeille : la requête de fil couvre tous
                // les dossiers, et la copie survit désormais à l'éviction (elle était masquée par accident).
                // Filtre volontairement COPIE-INDÉPENDANT : la queryKey de `useGetMailThread` est au niveau
                // MESSAGE, exempter « la copie ouverte » contaminerait la copie sœur Inbox. Ouvrir un mail
                // depuis Supprimés rend donc un fil vide → repli « mail ouvert » de MailFeed.
                // cf. docs/bugs/ab1900-eviction-copie-vs-message.md
                .filter(
                    (mail) =>
                        mail.parentFolderId !==
                        this.mailRecord.deleteditemsFolderId,
                )
                .sort(
                    (a, b) =>
                        new Date(a.sentDateTime!).getTime() -
                        new Date(b.sentDateTime!).getTime(),
                );

        // Write-through (warm-cache) : seed les links/events de chaque mail du thread dans le Record
        // view-content (présents dès le DTO BaseMail). Les attachments (metadata + previews) sont seedés
        // à l'enrichissement (getMailFullBodyContentWithAttachments) ; on seed dès maintenant ceux dont la
        // copie est DÉJÀ enrichie. Cf. _seedMailViewContentFromMail & Doc/AB#1727.
        for (const mail of sortedConversationMailsInternetMessageIds) {
            // Warm-cache : on ne seed QUE les mails DÉJÀ enrichis (bundle complet). Les autres seront seedés
            // à la fin de leur enrichissement (getMailFullBodyContentWithAttachments) ; tant qu'un mail n'est
            // pas complet, on NE crée PAS d'entrée Record — sinon on gèlerait /mails/content sur un contenu
            // partiel (cf. _seedMailViewContentFromMail). NB: le param `uniqueMailvistaId` masque la fonction
            // homonyme → on lit le champ stampé sur la copie canonique.
            const copyKey = mail.uniqueMailvistaId;
            const alreadyEnriched =
                copyKey !== undefined &&
                this.mailRecord._attachmentsFetchedSet.has(copyKey);
            if (alreadyEnriched) this._seedMailViewContentFromMail(mail);
        }

        logger.info(
            [LOGS.MailManager],
            "Sorted conversation mails",
            sortedConversationMailsInternetMessageIds,
        );

        // Fire-and-forget : le thread est retourné immédiatement, sans attendre les attachments.
        // PJ non-inline → panel Documents se remplit via mail-updated.
        // Images inline (CID) → résolues en background, mail-updated remplace l'objet complet
        //   dans le cache React Query → re-render propre (useGetMailThread handler vérifié).
        // Guard _attachmentsFetchedSet : skip les COPIES déjà enrichies (CustomView ou thread précédent).
        // Clé de niveau COPIE (uniqueMailvistaId de la copie canonique) : un self-sent a deux copies
        // sous le même internetMessageId ; enrichir l'une ne doit pas marquer l'autre.
        // Le mail d'entrée (celui que l'utilisateur a ouvert) est mis en tête de queue
        //   pour être résolu en priorité. Le marquage est fait par getMailFullBodyContentWithAttachments
        //   sur succès → on ne pré-marque plus ici.
        const mailsThreadNeedingFetch = [
            internetMessageId,
            ...conversationMailsInternetMessageIds.filter(
                (id) => id !== internetMessageId,
            ),
        ].filter((id) => {
            // Param `uniqueMailvistaId` masqué → on lit le champ stampé sur la copie canonique.
            const canonicalCopyKey =
                this.mailSync._getCanonicalMail(id)?.uniqueMailvistaId;
            return (
                canonicalCopyKey === undefined ||
                !this.mailRecord._attachmentsFetchedSet.has(canonicalCopyKey)
            );
        });

        if (mailsThreadNeedingFetch.length > 0) {
            const THREAD_CONCURRENCY = 3;
            let threadFetchIdx = 0;
            const threadWorker = async (): Promise<void> => {
                while (threadFetchIdx < mailsThreadNeedingFetch.length) {
                    const id = mailsThreadNeedingFetch[threadFetchIdx++];
                    await this.getMailFullBodyContentWithAttachments(id).catch(
                        (err) => {
                            logger.error(
                                [LOGS.MailManager],
                                `getMailThread: enrichissement échoué pour ${id}: ${err}`,
                            );
                        },
                    );
                }
            };
            void Promise.all(
                Array.from(
                    {
                        length: Math.min(
                            THREAD_CONCURRENCY,
                            mailsThreadNeedingFetch.length,
                        ),
                    },
                    () => threadWorker(),
                ),
            );
        }

        return sortedConversationMailsInternetMessageIds;
    }

    public async searchContact(searchString: string): Promise<string[]> {
        logger.info(
            [LOGS.MailManager],
            `searchContact containing ${searchString}`,
        );

        const { emails } =
            await this.contactManager.resolveContacts(searchString);
        return emails;
    }

    /**
     * Propage les pièces jointes d'une copie venant d'être
     * enrichie vers les copies SŒURS (même `internetMessageId`, dossier différent) encore vides.
     *
     * Contexte : un même message logique peut exister en plusieurs copies physiques (ex. self-sent
     * Exchange : Inbox + Sent), keyées par `parentFolderId` dans le Record. Les PJ sont IDENTIQUES sur
     * toutes les copies d'un même message → dès qu'une copie a ses PJ, les sœurs peuvent en hériter.
     *
     * Décisions appliquées :
     *  - C-1 : on ne propage QUE `attachments` + `hasAttachment` (jamais `message`/`uniqueBody` : le
     *          corps et ses images inline restent résolus par copie lors de SON propre enrichissement) ;
     *  - C-2 : on ne remplit QUE les sœurs vides (`attachments.length === 0`) — idempotent, pas de churn ;
     *  - cas positif uniquement : on ne propage que de VRAIES PJ → on ne « vide » jamais une sœur
     *          (protège contre une lecture source ayant renvoyé 0 PJ à tort, ex. id périmé) ;
     *  - C-4 : propagation EN MÉMOIRE (Record) uniquement → AUCUN `mail-updated` émis pour les sœurs
     *          (le front les verra au prochain fetch frais) — on ne surcharge pas le bus d'events ;
     *  - C-5a : on ne marque PAS les sœurs dans `_attachmentsFetchedSet` → si une sœur est réellement
     *           ouverte, son propre enrichissement pourra résoudre les images inline de SON corps.
     */
    private _propagateAttachmentsToSiblings(source: Mail): void {
        // Cas positif uniquement : rien à propager si la source n'a pas (ou plus) de PJ réelle.
        if ((source.attachments?.length ?? 0) === 0) return;

        const copies =
            this.mailRecord.byInternetMessageId[source.internetMessageId];
        if (!copies) return;

        for (const sibling of copies.values()) {
            if (sibling.parentFolderId === source.parentFolderId) continue; // sauter la copie source elle-même
            if ((sibling.attachments?.length ?? 0) > 0) continue; // ne pas écraser une sœur déjà enrichie

            // Copies d'un même internetMessageId ⇒ mêmes PJ → recopie directe. Shallow copy du tableau
            // (on partage les objets PJ, données d'affichage en lecture seule → pas de duplication des blobs).
            sibling.attachments = [...source.attachments];
            sibling.hasAttachment = true;
            sibling.componentSize =
                this.mailSync._determineMailComponentSize(sibling);
            logger.info(
                [LOGS.MailManager],
                `C(push): PJ propagées vers la copie sœur ${sibling.uniqueMailvistaId ?? sibling.parentFolderId} (depuis ${source.uniqueMailvistaId})`,
            );
        }
    }
}
